import { modalAnatomy as parts } from "@chakra-ui/anatomy";
import { PartsStyleFunction, PartsStyleObject, SystemStyleFunction, SystemStyleObject } from "@chakra-ui/theme-tools";

const baseStyleOverlay: SystemStyleObject = {
  bg: "blackAlpha.600",
  zIndex: "modal"
};

const baseStyleDialogContainer: SystemStyleFunction = (props) => {
  const { isCentered, scrollBehavior } = props;

  return {
    display: "flex",
    zIndex: "modal",
    justifyContent: "center",
    alignItems: isCentered ? "center" : "flex-start",
    overflow: scrollBehavior === "inside" ? "hidden" : "auto"
  };
};

const baseStyleDialog: SystemStyleFunction = (props) => {
  const { scrollBehavior } = props;

  return {
    borderRadius: "lg",
    bg: "surface2",
    color: "inherit",
    my: "3.75rem",
    zIndex: "modal",
    maxH: scrollBehavior === "inside" ? "calc(100% - 7.5rem)" : undefined,
    boxShadow: "elevation2",
    transition: "height 0.2s"
  };
};

const baseStyleHeader: SystemStyleObject = {
  px: 32,
  py: 16,
  fontSize: "2xl",
  fontWeight: "semibold"
};

const baseStyleCloseButton: SystemStyleObject = {
  position: "absolute",
  top: 2,
  insetEnd: 3,
  zIndex: 2
};

const baseStyleBody: SystemStyleFunction = (props) => {
  const { scrollBehavior } = props;
  return {
    px: 32,
    pb: 40,
    overflow: scrollBehavior === "inside" ? "auto" : undefined,
    display: "flex",
    flexDirection: "column",
    flexGrow: 1
  };
};

const baseStyleFooter: SystemStyleObject = {
  px: 32,
  pt: 24,
  pb: 16
};

const baseStyle: PartsStyleFunction<typeof parts> = (props) => ({
  overlay: baseStyleOverlay,
  dialogContainer: baseStyleDialogContainer(props),
  dialog: baseStyleDialog(props),
  header: baseStyleHeader,
  closeButton: baseStyleCloseButton,
  body: baseStyleBody(props),
  footer: baseStyleFooter
});

/**
 * Since the `maxWidth` prop references theme.sizes internally,
 * we can leverage that to size our modals.
 */
function getSize(value: string): PartsStyleObject<typeof parts> {
  if (value === "full") {
    return {
      dialog: {
        maxW: "100vw",
        minH: "100vh",
        "@supports(min-height: -webkit-fill-available)": {
          minH: "-webkit-fill-available"
        },
        my: 0,
        borderRadius: 0
      }
    };
  }
  return {
    dialog: { maxW: value }
  };
}

const sizes = {
  xs: getSize("xs"),
  sm: getSize("sm"),
  md: getSize("md"),
  lg: getSize("lg"),
  xl: getSize("xl"),
  "1xl": getSize("1xl"),
  "2xl": getSize("2xl"),
  "3xl": getSize("3xl"),
  "4xl": getSize("4xl"),
  "5xl": getSize("5xl"),
  "6xl": getSize("6xl"),
  full: getSize("full")
};

const defaultProps = {
  size: "md"
};

export default {
  parts: parts.keys,
  baseStyle,
  sizes,
  defaultProps
};
